import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { AlertTriangle, ArrowLeft, FileDown, FileText, Loader2, Save, Sparkles, Trash2 } from 'lucide-react'
import { useContract, useDeleteContract } from '../../../hooks/useContracts.js'
import { useMyCompanies } from '../../../hooks/useMyCompanies.js'
import { getContractFile } from '../../../services/api.js'
import { AMOUNT_LABELS, CONTRACT_FORMULAS, CONTRACT_STATUS, formatEuros, inputClass } from '../constants.js'

const CLIENT_FIELDS = [
  { key: 'companyName', label: 'Dénomination sociale', wide: true },
  { key: 'legalStatus', label: 'Forme juridique', placeholder: 'SAS, SARL…' },
  { key: 'capital', label: 'Capital social (€)' },
  { key: 'siret', label: 'N° d’immatriculation (SIRET)' },
  { key: 'ownerName', label: 'Représentant légal', placeholder: 'Prénom Nom' },
  { key: 'qualityLeader', label: 'Qualité', placeholder: 'Président, Gérant…' },
  { key: 'address', label: 'Adresse', wide: true },
  { key: 'postalCode', label: 'Code postal' },
  { key: 'city', label: 'Ville' },
]

const errorMessage = (error, fallback) => error?.response?.data?.error || fallback
const num = (value) => Number(String(value ?? '').replace(',', '.')) || 0

function toDraft(contract) {
  return {
    title: contract.title,
    formula: contract.formula,
    companyId: contract.company?.id ?? null,
    client: Object.fromEntries(CLIENT_FIELDS.map((f) => [f.key, contract.client?.[f.key] ?? ''])),
    amounts: Object.fromEntries(Object.keys(AMOUNT_LABELS).map((k) => [k, String(contract.amounts?.[k] ?? 0)])),
    specification: contract.specification ?? '',
  }
}

function toPayload(draft) {
  return {
    ...draft,
    amounts: Object.fromEntries(Object.entries(draft.amounts).map(([k, v]) => [k, num(v)])),
  }
}

/** Échéancier tel qu'il apparaîtra en Annexe 2 (TVA 20 %). */
function Schedule({ formula, amounts }) {
  const pkg = num(amounts.package)
  const rec = num(amounts.monthlyRecurring)
  const sub = num(amounts.monthlySubscription)
  const ttc = (v) => formatEuros(v * 1.2)
  const rows = {
    F3: [['40 % à la commande', pkg * 0.4], ['40 % le mois suivant', pkg * 0.4], ['20 % à la livraison', pkg * 0.2]],
    F24: [['24 mensualités de', pkg / 24]],
    A: [['Abonnement mensuel (24 mois)', sub]],
  }[formula.replace('R', '')] ?? []
  if (formula.endsWith('R')) rows.push(['Récurrent mensuel', rec])

  return (
    <ul className="m-0 list-none space-y-1 p-0 text-xs text-white/60">
      {rows.map(([label, value]) => (
        <li key={label} className="flex justify-between gap-3">
          <span>{label}</span>
          <span className="text-white/80">{formatEuros(value)} HT · {ttc(value)} TTC</span>
        </li>
      ))}
    </ul>
  )
}

/** Contrats > Contrats > un contrat : formule, prestataire, client, montants, Annexe 3, génération. */
export default function ContractEditorPage({ contractId }) {
  const navigate = useNavigate()
  const c = useContract(contractId)
  const { companies } = useMyCompanies()
  const deleteContract = useDeleteContract()
  const { contract } = c
  const [draft, setDraft] = useState(null)

  useEffect(() => {
    if (contract) setDraft(toDraft(contract))
  }, [contract?.updatedAt]) // eslint-disable-line react-hooks/exhaustive-deps

  if (c.isLoading || !draft) {
    return (
      <div className="flex items-center justify-center py-20 text-white/50">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    )
  }
  if (!contract) return <p className="text-sm text-red-300">Contrat introuvable.</p>

  const dirty = JSON.stringify(toPayload(draft)) !== JSON.stringify(toPayload(toDraft(contract)))
  const generating = ['queued', 'running'].includes(contract.generation?.status)
  const failed = contract.generation?.status === 'failed'
  const status = CONTRACT_STATUS[contract.status] ?? CONTRACT_STATUS.draft
  const formula = CONTRACT_FORMULAS.find((f) => f.value === draft.formula) ?? CONTRACT_FORMULAS[0]
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }))

  const save = (then) =>
    c.update.mutate({ id: contract.id, ...toPayload(draft) }, {
      onSuccess: () => (then ? then() : toast.success('Contrat enregistré')),
      onError: (e) => toast.error(errorMessage(e, 'Enregistrement impossible')),
    })

  const generate = () => {
    const run = () =>
      c.generate.mutate(contract.id, { onError: (e) => toast.error(errorMessage(e, 'Génération impossible')) })
    if (dirty) save(run)
    else run()
  }

  const download = async (format) => {
    const tab = format === 'pdf' ? window.open('', '_blank') : null
    try {
      const blob = await getContractFile({ id: contract.id, format })
      const url = URL.createObjectURL(blob)
      if (format === 'pdf') {
        if (tab) tab.location.href = url
        else window.open(url, '_blank')
      } else {
        const a = document.createElement('a')
        a.href = url
        a.download = `${contract.title}.docx`
        a.click()
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (e) {
      tab?.close()
      toast.error(errorMessage(e, 'Fichier indisponible'))
    }
  }

  const handleDelete = () => {
    if (!window.confirm('Supprimer ce contrat ?')) return
    deleteContract.mutate(contract.id, {
      onSuccess: () => navigate('/dashboard/contrats/contrats'),
      onError: (e) => toast.error(errorMessage(e, 'Suppression impossible')),
    })
  }

  return (
    <div className="space-y-4 pb-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <Link to="/dashboard/contrats/contrats" className="text-white/50 hover:text-white" aria-label="Retour">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <input
            value={draft.title}
            onChange={(e) => set({ title: e.target.value })}
            className="min-w-0 flex-1 bg-transparent text-xl font-semibold text-white outline-none"
          />
          <span className={`shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${status.tone}`}>{status.label}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={handleDelete} className="rounded-xl p-2 text-white/40 hover:text-red-300" aria-label="Supprimer">
            <Trash2 className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => save()}
            disabled={!dirty || c.update.isPending}
            className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-4 py-2 text-sm font-semibold text-white/80 disabled:opacity-40"
          >
            {c.update.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Enregistrer
          </button>
          <button
            type="button"
            onClick={generate}
            disabled={generating || c.generate.isPending}
            className="inline-flex items-center gap-2 rounded-xl bg-[#5b5cf4] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {generating || c.generate.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {generating ? 'Génération…' : contract.files.pdf ? 'Régénérer le contrat' : 'Générer le contrat'}
          </button>
        </div>
      </div>

      {contract.quote && (
        <p className="m-0 text-sm text-white/50">
          Repris du devis{' '}
          <Link to={`/dashboard/contrats/devis/${contract.quote.id}`} className="text-[#5b5cf4]/80 hover:text-[#5b5cf4]">
            {contract.quote.number ? `${contract.quote.number} – ` : ''}{contract.quote.title}
          </Link>
        </p>
      )}

      {contract.missing.length > 0 && (
        <div className="flex gap-2 rounded-2xl border border-amber-400/30 bg-amber-400/10 p-4 text-sm text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Pour générer le contrat, il manque {contract.missing.join(', ')}.</span>
        </div>
      )}
      {failed && (
        <p className="m-0 rounded-2xl border border-red-400/30 bg-red-400/10 p-4 text-sm text-red-200">
          La génération a échoué : {contract.generation.error || 'erreur inconnue'}
        </p>
      )}

      {contract.files.pdf && (
        <section className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#5b5cf4]/30 bg-[#5b5cf4]/5 p-5">
          <div className="text-sm text-white/80">
            <p className="m-0 font-semibold text-white">Contrat généré</p>
            <p className="m-0 mt-0.5 text-xs text-white/50">
              Le {new Date(contract.files.generatedAt).toLocaleString('fr-FR')}
              {contract.status === 'draft' && ' · modifié depuis : régénérez-le pour en tenir compte'}
            </p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => download('pdf')} className="inline-flex items-center gap-2 rounded-xl bg-[#5b5cf4] px-4 py-2 text-sm font-semibold text-white">
              <FileDown className="h-4 w-4" /> PDF
            </button>
            <button type="button" onClick={() => download('docx')} className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-4 py-2 text-sm font-semibold text-white/80">
              <FileText className="h-4 w-4" /> Word
            </button>
          </div>
        </section>
      )}

      <section className="space-y-3 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <h3 className="m-0 text-sm font-semibold uppercase tracking-wide text-white/50">Formule</h3>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          {CONTRACT_FORMULAS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => set({ formula: f.value })}
              className={`rounded-xl border p-3 text-left transition ${
                draft.formula === f.value ? 'border-[#5b5cf4]/60 bg-[#5b5cf4]/10' : 'border-white/10 bg-white/[0.02] hover:border-white/25'
              }`}
            >
              <p className="m-0 text-sm font-semibold text-white">{f.label}</p>
              <p className="m-0 mt-1 text-xs text-white/50">{f.description}</p>
            </button>
          ))}
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="space-y-3 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
          <h3 className="m-0 text-sm font-semibold uppercase tracking-wide text-white/50">Prestataire</h3>
          {companies.length === 0 ? (
            <p className="m-0 text-sm text-white/60">
              Aucune société : renseignez-la dans{' '}
              <Link to="/dashboard/compte/entreprise" className="text-[#5b5cf4]/80 underline">Compte &gt; Mon entreprise</Link>.
            </p>
          ) : (
            <select
              value={draft.companyId ?? ''}
              onChange={(e) => set({ companyId: e.target.value ? Number(e.target.value) : null })}
              className={inputClass}
            >
              <option value="">— Choisir —</option>
              {companies.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}
            </select>
          )}

          <h3 className="m-0 pt-2 text-sm font-semibold uppercase tracking-wide text-white/50">Montants</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {formula.amounts.map((key) => (
              <label key={key} className="block text-xs text-white/60">
                {AMOUNT_LABELS[key]}
                <input
                  inputMode="decimal"
                  value={draft.amounts[key]}
                  onChange={(e) => set({ amounts: { ...draft.amounts, [key]: e.target.value } })}
                  className={`${inputClass} mt-1`}
                />
              </label>
            ))}
          </div>
          <Schedule formula={draft.formula} amounts={draft.amounts} />
        </section>

        <section className="space-y-3 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
          <h3 className="m-0 text-sm font-semibold uppercase tracking-wide text-white/50">Client</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {CLIENT_FIELDS.map((field) => (
              <label key={field.key} className={`block text-xs text-white/60 ${field.wide ? 'sm:col-span-2' : ''}`}>
                {field.label}
                <input
                  value={draft.client[field.key]}
                  placeholder={field.placeholder}
                  onChange={(e) => set({ client: { ...draft.client, [field.key]: e.target.value } })}
                  className={`${inputClass} mt-1`}
                />
              </label>
            ))}
          </div>
          <p className="m-0 text-xs text-white/40">Forme juridique, capital ou SIRET vides : « (en cours de création) » dans le contrat.</p>
        </section>
      </div>

      <section className="space-y-3 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <h3 className="m-0 text-sm font-semibold uppercase tracking-wide text-white/50">Annexe 3 – Spécifications techniques et fonctionnelles</h3>
        <textarea
          value={draft.specification}
          onChange={(e) => set({ specification: e.target.value })}
          rows={12}
          className={`${inputClass} font-mono text-xs`}
          placeholder="Reprises du devis (et de Serge). Lignes « ## Titre » en gras, « - » en puces."
        />
      </section>
    </div>
  )
}
